import { EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import emblaCarousel from 'embla-carousel';

/**
 * @class MediaEmbed
 * @extends MessageBus
 * @description Manages an Embla v8 carousel for media embeds.
 * Embla is initialised as a factory function (not a class) directly on
 * the viewport element. Loop + mixed-width slides work correctly because
 * Embla measures each slide individually unlike Swiper.
 */
export default class MediaEmbed extends MessageBus {
  /**
   * @param {HTMLElement} element - The module's root container element.
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;

    /** @type {import('embla-carousel').EmblaCarouselType|null} */
    this.embla = null;

    /** @type {HTMLElement|null} */
    this.viewportEl = null;

    /** @type {HTMLElement|null} */
    this.prevBtn = null;

    /** @type {HTMLElement|null} */
    this.nextBtn = null;

    this.init();
  }

  /**
   * Initialize the module and wire up handlers.
   * @returns {this} The initialized MediaEmbed instance.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM references used by the component.
   * @returns {this} The MediaEmbed instance with cached DOM refs.
   */
  cacheDomReferences() {
    this.viewportEl = this.element.querySelector(
      SELECTORS.MEDIA_EMBED_VIEWPORT
    );
    this.prevBtn = this.element.querySelector(SELECTORS.MEDIA_EMBED_PREV);
    this.nextBtn = this.element.querySelector(SELECTORS.MEDIA_EMBED_NEXT);

    return this;
  }

  /**
   * Bind event handler methods to `this`.
   * @returns {this} The MediaEmbed instance with bound handlers.
   */
  setupHandlers() {
    this.handlePrevClick = this.onPrevClick.bind(this);
    this.handleNextClick = this.onNextClick.bind(this);
    this.handleSelect = this.onSelect.bind(this);
    this.handleSettle = this.onSettle.bind(this);

    return this;
  }

  /**
   * Initialise Embla and attach event listeners.
   * @returns {this} The MediaEmbed instance with Embla enabled.
   */
  enable() {
    if (!this.viewportEl) return this;
    this.embla = emblaCarousel(
      this.viewportEl,
      {
        loop: true,
        align: 'center',
        containScroll: false,
        dragFree: false
      },
      [WheelGesturesPlugin()]
    );

    this.prevBtn?.addEventListener(EVENTS.CLICK, this.handlePrevClick);
    this.nextBtn?.addEventListener(EVENTS.CLICK, this.handleNextClick);

    this.embla.on('select', this.handleSelect);
    this.embla.on('settle', this.handleSettle);

    this.updateArrows();

    return this;
  }

  /**
   * Destroy Embla and remove event listeners.
   * @returns {this} The MediaEmbed instance after teardown.
   */
  destroy() {
    this.embla?.destroy();
    this.prevBtn?.removeEventListener(EVENTS.CLICK, this.handlePrevClick);
    this.nextBtn?.removeEventListener(EVENTS.CLICK, this.handleNextClick);

    return this;
  }

  /**
   * Update prev/next button disabled state.
   * @returns {void}
   */
  updateArrows() {
    if (!this.embla || !this.prevBtn || !this.nextBtn) return;

    this.prevBtn.classList.toggle('is-disabled', !this.embla.canScrollPrev());
    this.nextBtn.classList.toggle('is-disabled', !this.embla.canScrollNext());
  }

  /**
   * Pause all videos in this carousel.
   * @returns {void}
   */
  pauseAllVideos() {
    this.element
      .querySelectorAll(SELECTORS.MEDIA_EMBED_VIDEO)
      .forEach((video) => {
        video.pause();
      });
  }

  /**
   * Scroll to previous slide.
   * @returns {void}
   */
  onPrevClick() {
    this.embla?.scrollPrev();
  }

  /**
   * Scroll to next slide.
   * @returns {void}
   */
  onNextClick() {
    this.embla?.scrollNext();
  }

  /**
   * Update arrows each time selected slide changes.
   * @returns {void}
   */
  onSelect() {
    this.updateArrows();
  }

  /**
   * Pause videos once carousel finishes scrolling.
   * @returns {void}
   */
  onSettle() {
    this.pauseAllVideos();
  }
}
